import React from 'react'
import { BlueprintDimensionInput } from './BlueprintDimensionInput'
import type { AnchorSpec, BlueprintDimKey, BlueprintLayout } from './types'

const INPUT_W = 56
const INPUT_H = 20
const TICK = 4
const LINE_STROKE = '#64748b' // slate-500
// Distance of the dimension line from the shape edge (px).
const OFFSET: Record<AnchorSpec['side'], number> = {
  top: 18,
  bottom: 20,
  left: 34,
  right: 34,
}

interface BlueprintDimensionOverlayProps {
  layout: BlueprintLayout
  anchors: AnchorSpec[]
  values: Partial<Record<BlueprintDimKey, number | null | undefined>>
  errors?: Partial<Record<BlueprintDimKey, string | undefined>>
  placeholders?: Partial<Record<BlueprintDimKey, string>>
  onCommit: (key: BlueprintDimKey, value: number | null) => void
  /** Preview/debug: outline the measured shape rect. */
  debugRect?: boolean
}

interface PlacedAnchor {
  anchor: AnchorSpec
  // dimension line endpoints (px)
  a: { x: number; y: number }
  b: { x: number; y: number }
  // extension lines from shape edge to dimension line
  ext: Array<[{ x: number; y: number }, { x: number; y: number }]>
  // optional leader from line end to a displaced input
  leader?: [{ x: number; y: number }, { x: number; y: number }]
  input: { left: number; top: number }
  /** Angular anchors: dashed guides (rays + small arc), drawn instead. */
  arc?: { rays: Array<[Pt, Pt]>; path: string }
}

type Pt = { x: number; y: number }

const ARC_GUIDE_PX = 34

/** Sweep clockwise from straight up around a centre (screen y points down). */
function placeAngle(anchor: AnchorSpec, layout: BlueprintLayout): PlacedAnchor {
  const { shapeRect: r, scale, container } = layout
  const spec = anchor.angle!
  const c = { x: r.x + spec.center.x * scale, y: r.y + spec.center.y * scale }
  const R = spec.radius * scale
  const sweep = (spec.sweepDeg * Math.PI) / 180
  const dir = (t: number) => ({ x: Math.sin(t), y: -Math.cos(t) })
  const at = (t: number, d: number) => ({
    x: c.x + dir(t).x * d,
    y: c.y + dir(t).y * d,
  })
  const g = Math.min(ARC_GUIDE_PX, Math.max(R * 0.6, 12))
  const start = at(0, g)
  const end = at(sweep, g)
  const largeArc = sweep > Math.PI ? 1 : 0
  // Input sits on the bisector, just past the guide arc, inside the sweep —
  // clear of the corner-radius input, which is outside the left edge.
  const mid = at(sweep / 2, g + 4 + INPUT_W / 2)
  return {
    anchor,
    a: c,
    b: c,
    ext: [],
    arc: {
      rays: [
        [c, at(0, R)],
        [c, at(sweep, R)],
      ],
      path: `M ${start.x} ${start.y} A ${g} ${g} 0 ${largeArc} 1 ${end.x} ${end.y}`,
    },
    input: {
      left: clamp(
        mid.x - INPUT_W / 2,
        0,
        Math.max(0, container.width - INPUT_W)
      ),
      top: clamp(
        mid.y - INPUT_H / 2,
        0,
        Math.max(0, container.height - INPUT_H)
      ),
    },
  }
}

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v))

function place(anchor: AnchorSpec, layout: BlueprintLayout): PlacedAnchor {
  if (anchor.angle) return placeAngle(anchor, layout)
  const { shapeRect: r, scale, container } = layout
  const toPx = (p: { x: number; y: number }) => ({
    x: r.x + p.x * scale,
    y: r.y + p.y * scale,
  })
  const pa = toPx(anchor.from)
  const pb = toPx(anchor.to)
  const horizontal = anchor.side === 'top' || anchor.side === 'bottom'
  const off = anchor.offsetPx ?? OFFSET[anchor.side]

  let a: { x: number; y: number }
  let b: { x: number; y: number }
  if (horizontal) {
    const y = anchor.side === 'top' ? r.y - off : r.y + r.height + off
    a = { x: pa.x, y }
    b = { x: pb.x, y }
  } else {
    const x = anchor.side === 'left' ? r.x - off : r.x + r.width + off
    a = { x, y: pa.y }
    b = { x, y: pb.y }
  }
  const ext: PlacedAnchor['ext'] = [
    [pa, a],
    [pb, b],
  ]

  const along = horizontal ? Math.abs(b.x - a.x) : Math.abs(b.y - a.y)
  const needed = (horizontal ? INPUT_W : INPUT_H) + 8
  let cx = (a.x + b.x) / 2
  let cy = (a.y + b.y) / 2
  let leader: PlacedAnchor['leader']

  if (along < needed) {
    // Too short to hold the input: push it past the end of the segment that
    // faces away from the shape's centre, with a short leader.
    const gap = 6
    if (horizontal) {
      const outwardLeft = cx < r.x + r.width / 2
      const end = outwardLeft ? Math.min(a.x, b.x) : Math.max(a.x, b.x)
      cx = outwardLeft ? end - gap - INPUT_W / 2 : end + gap + INPUT_W / 2
      leader = [
        { x: end, y: cy },
        { x: outwardLeft ? end - gap : end + gap, y: cy },
      ]
    } else {
      const outwardUp = cy < r.y + r.height / 2
      const end = outwardUp ? Math.min(a.y, b.y) : Math.max(a.y, b.y)
      cy = outwardUp ? end - gap - INPUT_H / 2 : end + gap + INPUT_H / 2
      leader = [
        { x: cx, y: end },
        { x: cx, y: outwardUp ? end - gap : end + gap },
      ]
    }
  }

  return {
    anchor,
    a,
    b,
    ext,
    leader,
    input: {
      left: clamp(cx - INPUT_W / 2, 0, Math.max(0, container.width - INPUT_W)),
      top: clamp(cy - INPUT_H / 2, 0, Math.max(0, container.height - INPUT_H)),
    },
  }
}

const tickPath = (p: { x: number; y: number }, horizontal: boolean) =>
  horizontal
    ? `M ${p.x} ${p.y - TICK} L ${p.x} ${p.y + TICK}`
    : `M ${p.x - TICK} ${p.y} L ${p.x + TICK} ${p.y}`

export function BlueprintDimensionOverlay({
  layout,
  anchors,
  values,
  errors,
  placeholders,
  onCommit,
  debugRect,
}: BlueprintDimensionOverlayProps) {
  const placed = anchors.map(anchor => place(anchor, layout))
  const { width, height } = layout.container
  const r = layout.shapeRect

  return (
    <>
      <svg
        className="pointer-events-none absolute inset-0"
        width={width}
        height={height}
        aria-hidden
      >
        {debugRect && (
          <rect
            x={r.x}
            y={r.y}
            width={r.width}
            height={r.height}
            fill="none"
            stroke="#f43f5e"
            strokeDasharray="4 3"
          />
        )}
        {placed.map(({ anchor, a, b, ext, leader, arc }) => {
          if (arc) {
            return (
              <g
                key={anchor.key}
                stroke={LINE_STROKE}
                strokeWidth={1}
                fill="none"
                strokeDasharray="3 3"
              >
                {arc.rays.map(([from, to], i) => (
                  <line
                    key={i}
                    x1={from.x}
                    y1={from.y}
                    x2={to.x}
                    y2={to.y}
                    opacity={0.6}
                  />
                ))}
                <path d={arc.path} />
                <circle cx={a.x} cy={a.y} r={2} fill={LINE_STROKE} />
              </g>
            )
          }
          const horizontal = anchor.side === 'top' || anchor.side === 'bottom'
          return (
            <g
              key={anchor.key}
              stroke={LINE_STROKE}
              strokeWidth={1}
              fill="none"
            >
              {ext.map(([from, to], i) => (
                <line
                  key={i}
                  x1={from.x}
                  y1={from.y}
                  x2={to.x}
                  y2={to.y}
                  strokeDasharray="2 2"
                  opacity={0.6}
                />
              ))}
              <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
              <path d={tickPath(a, horizontal)} />
              <path d={tickPath(b, horizontal)} />
              {leader && (
                <line
                  x1={leader[0].x}
                  y1={leader[0].y}
                  x2={leader[1].x}
                  y2={leader[1].y}
                />
              )}
            </g>
          )
        })}
      </svg>
      {placed.map(({ anchor, input }) => (
        <BlueprintDimensionInput
          key={anchor.key}
          label={anchor.label}
          value={values[anchor.key]}
          error={errors?.[anchor.key]}
          readOnly={anchor.readOnly}
          placeholder={placeholders?.[anchor.key]}
          unit={anchor.unit}
          hint={anchor.hint}
          onCommit={v => onCommit(anchor.key, v)}
          className="absolute"
          style={{ left: input.left, top: input.top }}
        />
      ))}
    </>
  )
}
